/**
 * Portal hover hit box for Explore Unknown vs `unknown_background.mp4`.
 *
 * We cannot see your video — this file is the single place to align the box with the portal in the art.
 * Layout is relative to the same inner container as the scaled game UI (logical 1920×1080 canvas).
 *
 * How to tune (pick one):
 *
 * 1) From your source video frame (recommended)
 *    - Use resolution that matches how you authored (often 1920×1080).
 *    - In an editor, draw a rectangle around the portal or mark its center + size in pixels.
 *    - centerX% = (centerX / frameWidth) * 100, centerY% = (centerY / frameHeight) * 100
 *    - width%  = (portalWidth / frameWidth) * 100, height% = (portalHeight / frameHeight) * 100
 *    - Put those into anchorLeftPercent, anchorTopPercent, widthPercent, heightPercent below.
 *
 * 2) Send us numbers
 *    - Message: center as % from left / top of the frame, then width % and height % of the frame,
 *      or a pixel rect (left, top, right, bottom) + frame width × height — we can convert.
 *
 * 3) Live in the browser
 *    - Set showLayoutGrid: true (see below) — same style of grid as BattleUI dev mode — hover and read
 *      "Cell (col, row)" / "Canvas px" in the tooltip, then tell us those values for the portal.
 *    - Set debugOutline: true, reload, adjust percentages until the dashed outline sits on the portal,
 *    - then set debugOutline back to false.
 *
 * Note: The video uses CSS object-fit: cover; edges of the file may be cropped on some aspect ratios.
 *       If the portal sits in a cropped region, tune using what you *see on screen* (option 3).
 *
 * Shadowverse scope (design target): on the order of ~500 encounters across ~100 maps once complete.
 * Per-map POIs and global completion live in layout/DB; per-run scoring uses `explore_unknown_runs`.
 */

export const EXPLORE_UNKNOWN_PORTAL_LAYOUT = {
  /** Aligned from grid: canvas rect (1052, 90)–(1534, 608) → center & size vs 1920×1080. */
  /** Horizontal center of the portal hit area (% of container width, 0–100). */
  anchorLeftPercent: (1293 / 1920) * 100,
  /** Vertical center of the portal hit area (% of container height, 0–100). */
  anchorTopPercent: (349 / 1080) * 100,
  /** Width of the hit area (% of container width). */
  widthPercent: (482 / 1920) * 100,
  /** Height of the hit area (% of container height). */
  heightPercent: (518 / 1080) * 100,
  /** While true, draws a faint outline so you can align the box to the video. */
  debugOutline: false,

  /**
   * BattleUI-style grid (set true temporarily to re-align).
   */
  showLayoutGrid: false,

  /**
   * Grid on top of shadowverse_arrival.jpg to mark waypoint positions (set false when done).
   */
  showArrivalLayoutGrid: false,

  /**
   * Grid on shadowverse_cave.jpg for tuning `cavePointsOfInterest` (1920×1080 canvas px).
   * Set false once coordinates are final.
   */
  showCaveLayoutGrid: false,

  /**
   * Grid on shadowverse_desert.jpg (set true while tuning six waypoints).
   */
  showDesertLayoutGrid: false,

  /**
   * Grid on shadowverse_fair.jpg (set true while tuning seven waypoints).
   */
  showFairLayoutGrid: false,

  /** Grid line spacing in logical pixels (matches BattleUI dev default). */
  gridSize: 20,

  /**
   * Points of interest on `shadowverse_arrival.jpg` (logical canvas px), connected in order.
   * Global mission indices 1–5.
   */
  arrivalPointsOfInterest: [
    [277, 572],
    [752, 570],
    [1273, 430],
    [1514, 349],
    [1318, 284],
  ],

  /**
   * The Beyond — alternative branch POIs (separate from normal path; fork after mission 1).
   * Global missions 1–5 normal; alt −1 plays four missions, alt −2 plays three.
   */
  arrivalAlternativeMinus1PointsOfInterest: [
    [277, 572],
    [457, 442],
    [619, 355],
    [531, 279],
  ],
  arrivalAlternativeMinus2PointsOfInterest: [
    [277, 572],
    [694, 451],
    [833, 330],
  ],

  /** First cave map (`shadowverse_cave.jpg`). Global indices 6–10. */
  cavePointsOfInterest: [
    [1835, 672],
    [1509, 596],
    [1240, 453],
    [1130, 329],
    [950, 269],
  ],
  caveAlternativeMinus1PointsOfInterest: [
    [1835, 672],
    [1618, 513],
    [1503, 397],
    [1357, 265],
  ],
  caveAlternativeMinus2PointsOfInterest: [
    [1835, 672],
    [911, 655],
    [239, 534],
  ],

  /**
   * Desert map (`shadowverse_desert.jpg`). Global indices 11–16 (six waypoints).
   */
  desertPointsOfInterest: [
    [974, 736],
    [778, 559],
    [897, 466],
    [1185, 460],
    [1334, 388],
    [1411, 263],
  ],
  desertAlternativeMinus1PointsOfInterest: [
    [975, 743],
    [1273, 675],
    [1481, 675],
    [1622, 659],
    [1778, 639],
  ],
  desertAlternativeMinus2PointsOfInterest: [
    [974, 736],
    [553, 618],
    [414, 532],
    [552, 258],
  ],

  arrivalMapImageSrc: '/images/shadowverse_arrival.jpg',
  caveMapImageSrc: '/images/shadowverse_cave.jpg',
  desertMapImageSrc: '/images/shadowverse_desert.jpg',

  /**
   * Fairgrounds map (`shadowverse_fair.jpg`) after the desert slice. Global mission indices 17–23 (seven waypoints).
   * Coordinates are 1920×1080 authoring space — tune with `showFairLayoutGrid` + dev waypoint editor.
   */
  fairMapImageSrc: '/images/shadowverse_fair.jpg',
  fairPointsOfInterest: [
    [968, 702],
    [340, 583],
    [681, 379],
    [987, 467],
    [1318, 647],
    [1665, 611],
    [1605, 495],
  ],
  fairAlternativeMinus1PointsOfInterest: [
    [968, 702],
    [812, 592],
    [866, 360],
    [1085, 311],
    [1375, 256],
    [1385, 367],
  ],
  fairAlternativeMinus2PointsOfInterest: [
    [968, 702],
    [1153, 642],
    [1213, 361],
    [1345, 482],
    [1501, 421],
  ],

  /**
   * Lavender field (`shadowverse_lavender.jpg`) after the fairgrounds slice. Global missions 24–28 (five waypoints).
   */
  lavenderMapImageSrc: '/images/shadowverse_lavender.jpg',
  lavenderPointsOfInterest: [
    [238, 542],
    [506, 468],
    [666, 450],
    [825, 459],
    [988, 500],
  ],
  /**
   * The Scent — alternative branch (fork after mission 24). Alt −1: four missions; alt −2: three.
   */
  lavenderAlternativeMinus1PointsOfInterest: [
    [238, 542],
    [636, 648],
    [1050, 657],
    [987, 502],
  ],
  lavenderAlternativeMinus2PointsOfInterest: [
    [238, 542],
    [776, 554],
    [988, 501],
  ],
  /**
   * Treetop map (`treetop.jpg`) after the lavender slice. Single continuation waypoint.
   */
  treetopMapImageSrc: '/images/treetop.jpg',
  treetopPointsOfInterest: [[1254,416]],
  /**
   * Boat map (`boat.jpg`) after the treetop slice. Global missions 30–34 (five waypoints).
   */
  boatMapImageSrc: '/images/boat.jpg',
  boatPointsOfInterest: [
    [630, 439],
    [755, 553],
    [926, 532],
    [1318, 654],
    [1756, 579],
  ],
  boatAlternativeMinus1PointsOfInterest: [
    [630, 439],
    [972, 324],
    [1337, 327],
    [1498, 345],
  ],
  boatAlternativeMinus2PointsOfInterest: [
    [630, 439],
    [1143, 666],
    [1591, 436],
  ],

  /**
   * Waypoints per arrival and cave surfaces. Desert uses `desertMapSliceMissionCount` (6).
   * Global missions: 1–5 arrival, 6–10 cave, 11–16 desert, 17–23 fair, 24–28 lavender.
   */
  mapSliceMissionCount: 5,

  /** Waypoints on `shadowverse_desert.jpg` (missions 11–16). */
  desertMapSliceMissionCount: 6,

  /** Waypoints on `shadowverse_fair.jpg` (missions 17–23). */
  fairMapSliceMissionCount: 7,

  /** Waypoints on `shadowverse_lavender.jpg` (missions 24–28). */
  lavenderMapSliceMissionCount: 5,
  /** Waypoints on `treetop.jpg` (mission 29). */
  treetopMapSliceMissionCount: 1,
  /** Waypoints on `boat.jpg` (missions 30–34). */
  boatMapSliceMissionCount: 5,

  /**
   * Pixel Past map (`shadowverse_pixelpast.jpg`) after the boat slice. Global missions 35–40 (six waypoints).
   * Coordinates are 1920×1080 authoring space — tune with `showPixelPastLayoutGrid` + dev waypoint editor.
   */
  pixelPastMapImageSrc: '/images/shadowverse_pixelpast.jpg',
  pixelPastPointsOfInterest: [
    [293, 565],
    [752, 558],
    [1156, 394],
    [1260, 569],
    [1484, 331],
    [1292, 233],
  ],
  pixelPastAlternativeMinus1PointsOfInterest: [
    [293, 565],
    [690, 485],
    [749, 356],
    [1052, 372],
    [952, 275],
  ],
  pixelPastAlternativeMinus2PointsOfInterest: [
    [293, 565],
    [892, 604],
    [1359, 566],
    [1348, 333],
  ],
  /** Waypoints on `shadowverse_pixelpast.jpg` (missions 35–40). */
  pixelPastMapSliceMissionCount: 6,

  /**
   * Grid on `shadowverse_pixelpast.jpg` (set true while tuning six waypoints).
   */
  showPixelPastLayoutGrid: false,

  /**
   * Kendrex Colony map (`shadowverse_kendrex.jpg`) after Pixel Past. Global missions 41–46 (six waypoints).
   */
  kendrexMapImageSrc: '/images/shadowverse_kendrex.jpg',
  kendrexPointsOfInterest: [[1050,688],[465,597],[686,345],[1401,330],[1436,588],[1761,664]],
  kendrexMapSliceMissionCount: 6,

  /**
   * Grid on `shadowverse_kendrex.jpg` (set true while tuning six waypoints).
   */
  showKendrexLayoutGrid: false,

  /**
   * Autobahn map (`shadowverse_autobahn.jpg`) after Kendrex Colony. Global missions 47–53 (seven waypoints).
   */
  autobahnMapImageSrc: '/images/shadowverse_autobahn.jpg',
  autobahnPointsOfInterest: [
    [967, 763],
    [969, 659],
    [960, 561],
    [961, 471],
    [960, 390],
    [956, 312],
    [959, 199],
  ],
  autobahnAlternativeMinus1PointsOfInterest: [
    [967, 763],
    [967, 611],
    [967, 517],
    [966, 423],
    [958, 351],
    [957, 252],
  ],
  autobahnAlternativeMinus2PointsOfInterest: [
    [967, 763],
    [968, 704],
    [997, 488],
    [932, 377],
    [1017, 232],
  ],
  autobahnMapSliceMissionCount: 7,

  /**
   * Grid on `shadowverse_autobahn.jpg` (set true while tuning seven waypoints).
   */
  showAutobahnLayoutGrid: false,

  /**
   * Production Hell map (`shadowverse_productionhell.jpg`) on the dark side of the moon after Autobahn.
   * Global missions 54–60 (seven waypoints).
   */
  productionHellMapImageSrc: '/images/shadowverse_productionhell.jpg',
  productionHellPointsOfInterest: [
    [663, 591],
    [1230, 604],
    [1311, 515],
    [1599, 603],
    [1222, 330],
    [777, 346],
    [507, 322],
  ],
  productionHellAlternativeMinus1PointsOfInterest: [
    [663, 591],
    [1284, 710],
    [1271, 625],
    [1640, 494],
    [818, 274],
    [569, 284],
  ],
  productionHellAlternativeMinus2PointsOfInterest: [
    [663, 591],
    [1123, 489],
    [1080, 399],
    [767, 420],
    [506, 385],
  ],
  productionHellMapSliceMissionCount: 7,

  /**
   * Grid on `shadowverse_productionhell.jpg` (set true while tuning seven waypoints).
   */
  showProductionHellLayoutGrid: false,

  /**
   * The Awakening map (`shadowverse_awakening.jpg`) after Production Hell.
   * Global missions 61–68 (eight waypoints).
   */
  awakeningMapImageSrc: '/images/shadowverse_awakening.jpg',
  awakeningPointsOfInterest: [
    [319, 537],
    [922, 686],
    [723, 562],
    [1002, 470],
    [1224, 602],
    [1557, 585],
    [1404, 230],
    [1014, 219],
  ],
  awakeningAlternativeMinus1PointsOfInterest: [
    [319, 537],
    [456, 325],
    [801, 409],
    [1177, 450],
    [1474, 533],
    [1454, 338],
    [1316, 182],
  ],
  awakeningAlternativeMinus2PointsOfInterest: [
    [319, 537],
    [593, 372],
    [667, 591],
    [846, 713],
    [1650, 612],
    [1457, 196],
  ],
  awakeningMapSliceMissionCount: 8,

  /** The Core (69–73): linear battle backgrounds only — no map POIs. */
  coreMapSliceMissionCount: 5,

  /**
   * Grid on `shadowverse_awakening.jpg` (set true while tuning eight waypoints).
   */
  showAwakeningLayoutGrid: false,

  /**
   * Grid on `shadowverse_lavender.jpg` (set true while tuning five waypoints).
   */
  showLavenderLayoutGrid: false,

  /**
   * Rite of respite sits beside the mission that unlocks it; position = that waypoint + offset (authoring px).
   * Tune offsets if the marker sits off-canvas on a given map.
   */
  darknessBargainOffsetBySurface: {
    arrival: [88, -52],
    cave: [-82, -48],
    desert: [92, -46],
    fair: [78, -52],
    lavender: [82, -50],
    kendrex: [86, -48],
    autobahn: [86, -48],
    productionhell: [86, -48],
    awakening: [86, -48],
  },

  /**
   * Global mission indices that represent **boss missions**. Boss waypoints are drawn as a
   * red warning triangle with a black "DANGER" label on a yellow plate above them, replacing
   * the usual circle marker on the surface map.
   *
   * Boss mission nodes:
   *   - 10 (cave surface, indices 6–10)
   *   - 16 (desert surface, indices 11–16)
   *   - 23 (fair surface, indices 17–23)
   *   - 27 (lavender surface, indices 24–28)
   *   - 33 (boat surface, indices 30–34)
   *   - 40 (pixel past surface, indices 35–40)
   *   - 46 (kendrex colony surface, indices 41–46)
   *   - 53 (autobahn surface, indices 47–53)
   *   - 59 (production hell surface, indices 54–60)
   *   - 68 (awakening surface, indices 61–68)
   */
  bossMissionIndices: [10, 16, 23, 27, 33, 40, 46, 53, 59, 68, 73, 74, 75],
  /** Dev (player1): jump to surface entry missions; server bulk-completes earlier missions first. */
  devSurfaceEntryMissionIndices: [69],
};

/**
 * Inclusive global mission index bounds for the fairgrounds surface (`fairMapImageSrc`, e.g. `shadowverse_fair.jpg`).
 * Derived from slice counts so authoring stays consistent when waypoint counts change.
 *
 * @returns {{ fairFirst: number, fairLast: number }}
 */
export function getExploreUnknownFairSliceMissionIndexInclusiveBounds() {
  const L = EXPLORE_UNKNOWN_PORTAL_LAYOUT;
  const mapSliceMissionCount = L.mapSliceMissionCount ?? 5;
  const desertMapSliceMissionCount = L.desertMapSliceMissionCount ?? 6;
  const fairMapSliceMissionCount = L.fairMapSliceMissionCount ?? 7;
  const fairFirst = mapSliceMissionCount * 2 + desertMapSliceMissionCount + 1;
  const fairLast = fairFirst + fairMapSliceMissionCount - 1;
  if (!Number.isInteger(fairFirst) || !Number.isInteger(fairLast) || fairLast < fairFirst) {
    throw new Error('Explore Unknown portal layout: invalid fair slice mission bounds');
  }
  return { fairFirst, fairLast };
}

/**
 * @param {unknown} missionIndex - Global Explore Unknown mission index from `gameState.exploreUnknownMissionIndex`.
 * @returns {boolean}
 */
export function isExploreUnknownFairSliceMissionIndex(missionIndex) {
  const idx = Number(missionIndex);
  if (!Number.isInteger(idx)) {
    return false;
  }
  const { fairFirst, fairLast } = getExploreUnknownFairSliceMissionIndexInclusiveBounds();
  return idx >= fairFirst && idx <= fairLast;
}
